// Date validation and formatting utilities
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { t } from "i18next";

dayjs.extend(customParseFormat);

// Format date input with auto-slash insertion
export const formatDateInput = (value) => {
  // Remove all non-numeric characters except slashes
  let cleaned = value.replace(/[^0-9/]/g, "");

  // // Remove multiple consecutive slashes
  cleaned = cleaned.replace(/\/+/g, "/");

  // // Auto-add slashes at appropriate positions
  if (cleaned.length >= 3 && cleaned.charAt(2) !== "/") {
    cleaned = cleaned.substring(0, 2) + "/" + cleaned.substring(2);
  }
  if (cleaned.length >= 6 && cleaned.charAt(5) !== "/") {
    cleaned = cleaned.substring(0, 5) + "/" + cleaned.substring(5);
  }

  // Limit to DD/MM/YYYY format (10 characters)
  cleaned = cleaned.substring(0, 10);

  return cleaned;
};

// Validate if date is in correct format and not in future
export const validateDate = (dateString, allowFutureDates = false) => {
  if (!dateString) return { isValid: true, error: "" };

  // Check if format is complete DD/MM/YYYY
  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateString.match(dateRegex);

  if (!match) {
    if (dateString.length === 10) {
      return { isValid: false, error: t('PleaseEnterValidDate') };
    }
    return { isValid: true, error: "" }; // Still typing
  }

  const [, day, month, year] = match;
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  // Basic range validation
  if (monthNum < 1 || monthNum > 12) {
    return { isValid: false, error: "月份必須在01-12之間" };
  }

  if (dayNum < 1 || dayNum > 31) {
    return { isValid: false, error: "日期必須在01-31之間" };
  }

  // Use dayjs to validate the date more reliably
  const inputDate = dayjs(`${day}/${month}/${year}`, "DD/MM/YYYY", true);

  // Check if date is valid (handles leap years, days in month, etc.)
  if (!inputDate.isValid()) {
    return { isValid: false, error: "請輸入有效的日期" };
  }

  // Check if date is not in the future (unless allowFutureDates is true)
  if (!allowFutureDates) {
    const today = dayjs().endOf("day");

    if (inputDate.isAfter(today)) {
      return { isValid: false, error: "日期不能晚於今天" };
    }
  }

  // Check if year is reasonable (not too far in past or future)
  const currentYear = dayjs().year();
  const minYear = 1900;
  const maxYear = allowFutureDates ? currentYear + 100 : currentYear;
  
  if (yearNum < minYear || yearNum > maxYear) {
    return { isValid: false, error: `年份必須在${minYear}-${maxYear}之間` };
  }

  return { isValid: true, error: "" };
};

// Handle date input change with formatting and validation
export const handleDateChange = (value, setFieldValue, setError = null, allowFutureDates = false) => {
  const formattedValue = formatDateInput(value);
  setFieldValue(formattedValue);

  if (setError) {
    const validation = validateDate(formattedValue, allowFutureDates);
    setError(validation.error);
  }

  return formattedValue;
};

// Convert DD/MM/YYYY to ISO date for backend
export const convertToISO = (dateString, allowFutureDates = false) => {
  if (!dateString) return "";

  const validation = validateDate(dateString, allowFutureDates);
  if (!validation.isValid) return "";

  // Use dayjs with strict parsing to ensure DD/MM/YYYY format
  const parsedDate = dayjs(dateString, "DD/MM/YYYY", true);
  return parsedDate.isValid() ? parsedDate.toISOString() : ""; 
};

// Convert ISO date to DD/MM/YYYY for display
export const convertFromISO = (isoString) => {
  if (!isoString) return "";

  // First try parsing as ISO date
  let date = dayjs(isoString);
  
  // If that fails, try parsing as DD-M-YYYY or D-M-YYYY format
  if (!date.isValid()) {
    date = dayjs(isoString, ["DD-M-YYYY", "D-M-YYYY", "DD-MM-YYYY", "D-MM-YYYY"], true);
  }
  
  return date.isValid() ? date.format("DD/MM/YYYY") : "";
};
